import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { UserActions } from '@storeOld/reducers/user/actions'
import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import {
  isTeamOrAgencyPlan,
  PLAN_AGENCY,
  PLAN_PRO,
  PLAN_PRO2,
  PLAN_PRO3,
  PLAN_TEAM,
  PLAN_TEAM3SEATS,
  PLAN_TEAMPERSEAT,
  PLAN_TEAMPERSEAT2,
} from '@/constants/userSubscriptionPlans'
import { getPermissionsMatrixByUserType } from '@/permissions'
import {
  getPermissionsMatrix,
  type PROJECT_PERMISSION,
  type ROLE_TYPE,
} from '@/permissions/project/permissions'
import type {
  Member,
  RelatedAccountType,
  RelatedUserType,
  Team,
} from '@/types/models'

const getRelatedAccountType = (
  ownerId: string | null,
  ownerPlan: string | null
): RelatedAccountType => {
  if (ownerId === null) {
    return 'TRIAL'
  }

  switch (ownerPlan) {
    case PLAN_PRO:
    case PLAN_PRO2:
    case PLAN_PRO3:
      return 'PRO'

    case PLAN_TEAM:
    case PLAN_AGENCY:
    case PLAN_TEAMPERSEAT:
    case PLAN_TEAMPERSEAT2:
    case PLAN_TEAM3SEATS:
      return 'TEAM_AGENCY'

    default:
      return 'FREE'
  }
}

const getUserType = (
  ownerId: string | null,
  ownerPlan: string | null,
  team: Team | null,
  userId: string | null,
  authenticated: boolean,
  userTeams: Team[],
  members: Member[]
): ROLE_TYPE => {
  const accountType = getRelatedAccountType(ownerId, ownerPlan)
  const userType = getRelatedUserType(
    authenticated,
    ownerId,
    ownerPlan,
    team,
    userId,
    userTeams,
    members
  )

  return [accountType, userType].join('_') as ROLE_TYPE
}

const getRelatedUserType = (
  authenticated: boolean,
  ownerId: string | null,
  ownerPlan: string | null,
  team: Team | null,
  userId: string | null,
  userTeams: Team[],
  members: Member[]
): RelatedUserType => {
  if (authenticated) {
    if (ownerId === userId) {
      return 'OWNER'
    }

    if (
      ownerPlan &&
      team &&
      isTeamOrAgencyPlan(ownerPlan) &&
      userTeams.filter((t: Team) => t.id === team?.id).length
    ) {
      return 'MEMBER'
    }

    if (members.findIndex((member: Member) => member.id === userId) !== -1) {
      return 'PROJECT_MEMBER'
    }

    return 'LOGGED'
  }

  return 'ANONYMOUS'
}

const useProjectPermissions = () => {
  const dispatch = useDispatch()

  const ownerId = useSelector(migrationProjectSelector.getProjectOwnerId)
  const ownerPlan = useSelector(migrationProjectSelector.getProjectOwnerPlan)

  const team = useSelector(migrationProjectSelector.getProjectTeam)
  const members = useSelector(migrationProjectSelector.getProjectMembers)

  const { userId, authenticated, userTeams } = useSelector<
    AppState,
    {
      userId: string | null
      authenticated: boolean
      userTeams: Team[]
    }
  >(
    ({ auth }) => ({
      userId: auth.id,
      authenticated: auth.authenticated,
      userTeams: auth.teams,
    }),
    shallowEqual
  )

  useEffect(() => {
    const type = getUserType(
      ownerId,
      ownerPlan,
      team,
      userId,
      authenticated,
      userTeams,
      members
    )

    const userPermissions = getPermissionsMatrixByUserType<
      ROLE_TYPE,
      PROJECT_PERMISSION
    >(type, getPermissionsMatrix())

    dispatch({
      type: UserActions.SET_PERMISSIONS,
      payload: userPermissions,
    })
  }, [ownerId, ownerPlan, team, userId, authenticated, userTeams, members])
}

export default useProjectPermissions
